import React from 'react';
import Lottie from 'react-lottie';
import EmergeMultiSignApproved from '@/assets/EmergeMultiSignApproved.json';
import EmergeBatchDeclined from '@/assets/EmergeBatchDeclined.json';
import EmergePaymentScheduled from '@/assets/EmergePaymentScheduled.json';
import EmergePaymentSuccess from '@/assets/EmergePaymentSuccess.json';
import EmergePaymentFailure from '@/assets/EmergePaymentFailure.json';
import Button from '@/components/Button';
import { getErrorMessage, PaymentErrorCodeEnum } from '@/features/payments/utils/errorCodes';
import {
  batchOverviewModal,
  scheduledPaymentModal,
  scheduledTransferModal
} from '@/utils/translations';

export type TPaymentOutcomeVariant =
  | 'paymentApproved'
  | 'paymentScheduled'
  | 'paymentRejected'
  | 'paymentFailed'
  | 'paymentComplete'
  | 'transferFailed'
  | 'transferComplete'
  | 'batchApproved'
  | 'batchScheduled'
  | 'batchComplete'
  | 'batchFailed'
  | 'batchInsufficientFunds'
  | 'batchRejected';

interface PaymentOutcomeResultProps {
  variant: TPaymentOutcomeVariant;
  onClose: () => void;
  /** Labels the surrounding dialog - this heading replaces the modal's own title. */
  titleId?: string;
  /** Only used by `batchRejected`, whose body names the batch. */
  batchName?: string;
  /** Only used by `paymentFailed`/`transferFailed`/`paymentComplete`/`transferComplete`/
   *  `batchInsufficientFunds`, whose body names the account the run happened on. */
  accountName?: string;
}

const bodyLine = (text: string) => (
  <p className="text-sm font-normal leading-5 text-dark-navy-30">{text}</p>
);

type TOutcomeBodyParams = { batchName?: string; accountName?: string };

type TOutcomeConfig = {
  animationData: object;
  title: string;
  body: (params: TOutcomeBodyParams) => React.ReactNode;
  buttonLabel: string;
};

const CONFIG: Record<TPaymentOutcomeVariant, TOutcomeConfig> = {
  // This approval still leaves other approvers required - no badge, since the payment isn't fully
  // scheduled yet from this signatory's perspective (see hasApprovedLocally).
  paymentApproved: {
    animationData: EmergeMultiSignApproved,
    title: scheduledPaymentModal.approvedFinalTitle,
    body: () => (
      <>
        {bodyLine(scheduledPaymentModal.approvedFinalBodyLine1)}
        {bodyLine(scheduledPaymentModal.approvedFinalBodyLine2)}
      </>
    ),
    buttonLabel: scheduledPaymentModal.doneButton
  },
  // This approval was the last one required - no more approvers, the payment is fully scheduled
  // (see hasFinalApproval). Reuses the same badged animation as batchScheduled below.
  paymentScheduled: {
    animationData: EmergePaymentScheduled,
    title: scheduledPaymentModal.finalApprovalTitle,
    body: () => (
      <>
        {bodyLine(scheduledPaymentModal.finalApprovalBodyLine1)}
        {bodyLine(scheduledPaymentModal.finalApprovalBodyLine2)}
      </>
    ),
    buttonLabel: scheduledPaymentModal.doneButton
  },
  paymentRejected: {
    animationData: EmergeBatchDeclined,
    title: scheduledPaymentModal.rejectedTitle,
    body: () => (
      <>
        {bodyLine(scheduledPaymentModal.rejectedBodyLine1)}
        {bodyLine(scheduledPaymentModal.rejectedBodyLine2)}
      </>
    ),
    buttonLabel: scheduledPaymentModal.closeButton
  },
  // The final required approval on an individual scheduled payment also runs that day's payment
  // synchronously - this is what shows instead of paymentScheduled when that run itself failed
  // (see MultiSignErrorCodeEnum.EXECUTION_FAILED).
  paymentFailed: {
    animationData: EmergePaymentFailure,
    title: scheduledPaymentModal.paymentFailedTitle,
    body: ({ accountName }) => bodyLine(scheduledPaymentModal.paymentFailedBody(accountName ?? '')),
    buttonLabel: scheduledPaymentModal.closeButton
  },
  // Shown when Pay now (retry()) itself succeeds - see useScheduledPaymentAction's hasRetried.
  // Reuses the same success animation as batchComplete below.
  paymentComplete: {
    animationData: EmergePaymentSuccess,
    title: scheduledPaymentModal.paymentCompleteTitle,
    body: ({ accountName }) =>
      bodyLine(scheduledPaymentModal.paymentCompleteBody(accountName ?? '')),
    buttonLabel: scheduledPaymentModal.closeButton
  },
  // Shown for a scheduled transfer's Transfer now retry when the backend confirms
  // PaymentErrorCodeEnum.INSUFFICIENT_FUNDS (4017) - see useScheduledPaymentAction's
  // hasImmediateFailure handling in retry().
  transferFailed: {
    animationData: EmergePaymentFailure,
    title: scheduledTransferModal.transferFailedTitle,
    body: ({ accountName }) =>
      bodyLine(scheduledTransferModal.transferFailedBody(accountName ?? '')),
    buttonLabel: scheduledTransferModal.closeButton
  },
  // Shown when Transfer now (retry()) itself succeeds - see useScheduledPaymentAction's
  // hasRetried.
  transferComplete: {
    animationData: EmergePaymentSuccess,
    title: scheduledTransferModal.transferCompleteTitle,
    body: ({ accountName }) =>
      bodyLine(scheduledTransferModal.transferCompleteBody(accountName ?? '')),
    buttonLabel: scheduledTransferModal.closeButton
  },
  // This approval still leaves other approvers required - no badge, same reasoning as the
  // individual-payment paymentApproved variant above (see hasApprovedLocally).
  batchApproved: {
    animationData: EmergeMultiSignApproved,
    title: batchOverviewModal.batchOutcome.approvedTitle,
    body: () => (
      <>
        {bodyLine(batchOverviewModal.batchOutcome.approvedBodyLine1)}
        {bodyLine(batchOverviewModal.batchOutcome.approvedBodyLine2)}
      </>
    ),
    buttonLabel: scheduledPaymentModal.closeButton
  },
  batchScheduled: {
    animationData: EmergePaymentScheduled,
    title: batchOverviewModal.batchOutcome.scheduledTitle,
    body: () => (
      <>
        {bodyLine(batchOverviewModal.batchOutcome.scheduledBodyLine1)}
        {bodyLine(batchOverviewModal.batchOutcome.scheduledBodyLine2)}
      </>
    ),
    buttonLabel: scheduledPaymentModal.closeButton
  },
  batchComplete: {
    animationData: EmergePaymentSuccess,
    title: batchOverviewModal.batchOutcome.completeTitle,
    body: () => bodyLine(batchOverviewModal.batchOutcome.completeBody),
    buttonLabel: scheduledPaymentModal.closeButton
  },
  batchFailed: {
    animationData: EmergePaymentFailure,
    title: batchOverviewModal.batchOutcome.failedTitle,
    body: () => bodyLine(batchOverviewModal.batchOutcome.failedBody),
    buttonLabel: scheduledPaymentModal.closeButton
  },
  // Shown instead of batchFailed when the failed batch's total is more than the pay-from account's
  // live balance (see BatchOverviewModal) - the backend keeps no insufficient-funds state for a
  // batch, so it's inferred. Same copy as the batch submission's insufficient-funds result, so the
  // two never read differently.
  batchInsufficientFunds: {
    animationData: EmergePaymentFailure,
    title: batchOverviewModal.batchOutcome.failedTitle,
    body: ({ accountName }) =>
      bodyLine(getErrorMessage(PaymentErrorCodeEnum.INSUFFICIENT_FUNDS, accountName)),
    buttonLabel: scheduledPaymentModal.closeButton
  },
  batchRejected: {
    animationData: EmergeBatchDeclined,
    title: batchOverviewModal.batchOutcome.rejectedTitle,
    body: ({ batchName }) => (
      <>
        {bodyLine(batchOverviewModal.batchOutcome.rejectedBodyLine1(batchName ?? ''))}
        {bodyLine(batchOverviewModal.batchOutcome.rejectedBodyLine2)}
      </>
    ),
    buttonLabel: scheduledPaymentModal.closeButton
  }
};

/**
 * Full-screen takeover rendered in place of a modal's own content once there's an outcome from
 * this session's own approve/reject/retry action (or, for a batch, an IN_PROGRESS run resolving
 * while the modal stayed open) to show instead of the normal detail view. Most variants are
 * terminal - the last-required approval, a rejection, a failure - but `paymentApproved`/
 * `batchApproved` are NOT: they cover a non-final approval (hasApprovedLocally), where the
 * action itself is still PENDING and other signatories are still required - only this viewer's
 * part in it is done. One component instead of one per outcome, since the shell - Lottie, title,
 * body, single button - is identical throughout; only the asset/copy/button label differ per
 * variant.
 */
const PaymentOutcomeResult: React.FC<PaymentOutcomeResultProps> = ({
  variant,
  onClose,
  titleId,
  batchName,
  accountName
}) => {
  const config = CONFIG[variant];

  return (
    <div className="flex flex-col items-center rounded-2xl border border-aux-gray bg-white px-6 pb-6 pt-[77px]">
      <div className="h-[220px] w-[220px]">
        <Lottie
          options={{
            loop: false,
            autoplay: true,
            animationData: config.animationData,
            rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
          }}
        />
      </div>

      <div className="flex flex-col gap-y-1 pb-8 pt-12 text-center">
        <h2 id={titleId} className="text-xl font-medium text-emerge-navy">
          {config.title}
        </h2>
        {config.body({ batchName, accountName })}
      </div>

      <Button variant="primary" size="pill" onClick={onClose} className="w-full">
        {config.buttonLabel}
      </Button>
    </div>
  );
};

export default PaymentOutcomeResult;
